import * as React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  SDG_LABELS,
  sdgIconSrc,
  type ApiErrorResponse,
  type InternalHeaderProps,
  type ProjectResponse,
} from '@/types';
import {
  Building,
  Play,
  X,
  Loader2,
  CircleCheck,
  RotateCcw,
} from 'lucide-react';

import { ProjectMenu } from '../../project-menus';
import { buildProjectMenuItems } from '../../project-menu-config';

import { useProjectsMenuService } from '@/hooks/use-projects-menu-service';
import {
  ConfirmDialog,
  type ConfirmDialogConfig,
} from '@/components/confrim-dialog';
import { AvatarEditor } from '@/components/avatar-editor';
import { ProjectCuHandler } from '../../project-cu-handler';
import AssignMentor from '../../assign-mentory';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import { getStatusColor, getStatusLabel } from '@/lib';
import { AddSdgsDialog } from '@/components/sdgs/add-sdgs-dialog';
import {
  useUpdateOrAddInternalProjectSDGSMutation,
  useStartProjectMutation,
  useCompleteProjectMutation,
  useMarkAsInProgressMutation,
  useStartEvaluationMutation,
  useStartExternalEvaluationMutation,
  useStartExternalGroupProjectMutation,
} from '@/state';
import { useUpdateExternalProjectMutation } from '@/state/project/project-api';
import { useTenant } from '@/hooks';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { EvaluationModal } from './evaluation-modal';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import ShowBoards from '../../show-boards';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  // useGetPublicAdminClientsQuery,
  useGetPublicAdminClientScopeOptionsQuery,
} from '@/state/public';
import { useSearchParams } from 'react-router-dom';

export const InternalHeader = ({
  project,
  internalProjectId,
}: InternalHeaderProps) => {
  const [searchParams] = useSearchParams();
  const {
    // state
    openMenuId,
    setOpenMenuId,
    showUpdateProject,
    setShowUpdateProject,
    selectedProject,
    openFor,
    setOpenFor,
    showDialog,
    setShowDialog,
    image,
    confirmOpen,
    setConfirmOpen,
    confirmConfig,
    setConfirmConfig,
    mentorSheetProject,
    setMentorSheetProject,
    currentUser,

    // loading / error
    deletingLoading,
    thumbnailLoading,
    duplicateLoading,
    assignLoading,
    updateThumbnailExternalLoading,
    thumbnailError,

    // handlers
    handleEdit,
    handleThumbnailChange,
    handleDelete,
    updateThumbnailBanner,
    handleDuplicate,
    handleAssignMentor,
  } = useProjectsMenuService({ projectType: (searchParams.get('type') as 'INTERNAL' | 'EXTERNAL') || 'INTERNAL' });
  const { tenant } = useTenant();
  const [isAssignDrawerOpen, setIsAssignDrawerOpen] = React.useState(false);
  const [assignDrawerTab, setAssignDrawerTab] = React.useState<'scopes' | 'institutes'>('scopes');
  const [selectedScopeId, setSelectedScopeId] = React.useState<number | null>(null);
  const [selectedInstituteId, setSelectedInstituteId] = React.useState<number | null>(null);
  const [assignToCogknitAdmin, setAssignToCogknitAdmin] = React.useState(false);

  const [showEvaluation, setShowEvaluation] = React.useState(false);
  const [hideEvaluationPrompt, setHideEvaluationPrompt] = React.useState(false);
  const [showBoards, setShowBoards] = React.useState<ProjectResponse | null>(
    null
  );
  const projectType = searchParams.get('type');
  // const projectResourcesIdFromUrl = searchParams.get('projectResourcesId');
  const effectiveProjectId = internalProjectId || project.project_id || project.id;

  const [addOrUpdateSDGS, { isLoading: sdgsLoading, error: sdgsError }] =
    useUpdateOrAddInternalProjectSDGSMutation();

  const [startProject, { isLoading: isStartingProject }] =
    useStartProjectMutation();
  const [completeProject, { isLoading: isCompletingProject }] =
    useCompleteProjectMutation();
  const [markAsInProgress, { isLoading: isMarkingInProgress }] =
    useMarkAsInProgressMutation();
  const [startEvaluation, { isLoading: isStartingEvaluation }] =
    useStartEvaluationMutation();
  const [startExternalEvaluation, { isLoading: isStartingExternalEvaluation }] =
    useStartExternalEvaluationMutation();
  const [startExternalGroupProject, { isLoading: isStartingExternalProject }] =
    useStartExternalGroupProjectMutation();
  const [assignProject, { isLoading: isAssigningProject }] =
    useUpdateExternalProjectMutation();
  // const { data: clients = [] } = useGetPublicAdminClientsQuery();
  const { data: clientScopeOptions = [] } = useGetPublicAdminClientScopeOptionsQuery();

  const { isStudent, isCompany } = useClientPermissions();

  const isMentor = React.useMemo(() => {
    if (!currentUser?.email || !project.project_mentor) return false;
    return (
      currentUser.email.toLowerCase() === project.project_mentor.toLowerCase()
    );
  }, [currentUser?.email, project.project_mentor]);


  const status = Number(project.project_status);
  const isExternal =
    projectType === 'EXTERNAL' ||
    Number(project?.project_type) === 2 ||
    (project as any).public_project_candidates?.length > 0;
  const isPublished = Boolean(project.published);
  const hasAssignedSchools =
    Array.isArray(project.assigned_schools)
      ? project.assigned_schools.length > 0
      : Boolean(project.assigned_schools);
  const canShowPublishAction = isCompany && !isPublished && !hasAssignedSchools;
  const evaluationRequired = Boolean((project as any)?.evaluation_required);

  const canStart = isExternal ? status === 1 : status === 3;
  const canComplete = isExternal ? (status === 2 || status === 4) : status === 4;
  const canReopen = status === 5;
  const availableInstitutes = React.useMemo(
    () =>
      clientScopeOptions.filter((client) => {
        const schema = client.schema_name.trim().toLowerCase();
        return schema !== 'public' && schema !== 'cogknit';
      }),
    [clientScopeOptions]
  );
  const scopeCards = React.useMemo(() => {
    const scopeMap = new Map<number, { id: number; name: string; description: string; institutes: typeof availableInstitutes }>();
    availableInstitutes.forEach((client) => {
      client.client_scope.forEach((scope) => {
        const existing = scopeMap.get(scope.id);
        if (existing) existing.institutes.push(client);
        else scopeMap.set(scope.id, { id: scope.id, name: scope.name, description: scope.description, institutes: [client] });
      });
    });
    return Array.from(scopeMap.values());
  }, [availableInstitutes]);
  const visibleInstitutes = React.useMemo(() => {
    if (!selectedScopeId) return availableInstitutes;
    return availableInstitutes.filter((client) =>
      client.client_scope.some((scope) => scope.id === selectedScopeId)
    );
  }, [availableInstitutes, selectedScopeId]);
  // const assignedInstitutes = (project?.assigned_schools ?? [])
  //   .map((clientId) => clients.find((client) => client.id === clientId))
  //   .filter(Boolean);
  const resetAssignDrawer = React.useCallback(() => {
    setAssignDrawerTab('scopes');
    setSelectedScopeId(null);
    setSelectedInstituteId(null);
    setAssignToCogknitAdmin(false);
  }, []);
  const handleAssignDrawerChange = React.useCallback((open: boolean) => {
    setIsAssignDrawerOpen(open);
    if (!open) resetAssignDrawer();
  }, [resetAssignDrawer]);
  const handleInstituteSelect = (instituteId: number) => {
    if (assignToCogknitAdmin) return;
    setSelectedInstituteId((currentId) => (currentId === instituteId ? null : instituteId));
  };
  const canSubmitAssignment = assignToCogknitAdmin || selectedInstituteId !== null;
  const handleAssignSubmit = async () => {
    if (!project || !canSubmitAssignment || isAssigningProject) return;
    try {
      const data = selectedInstituteId !== null
        ? { assigned_schools: [selectedInstituteId], published: true }
        : { published: true };

      await assignProject({
        tenant: tenant!,
        projectId: effectiveProjectId,
        data,
      }).unwrap();
      toast.success('Project assignment updated successfully.');
      handleAssignDrawerChange(false);
    } catch (err) {
      toast.error('Unable to update the project assignment. Please try again.');
    }
  };

  const handleStartProjectAction = async () => {
    try {
      if (projectType === 'EXTERNAL') {
        await startExternalGroupProject({
          projectId: effectiveProjectId,
          data: { project_status: 2 },
        }).unwrap();
      } else {
        await startProject({
          tenant: tenant!,
          projectId: effectiveProjectId,
        }).unwrap();
      }
      toast.success('Project started successfully');
    } catch (error) {
      toast.error('Failed to start project');
    }
  };

  const handleCompleteProjectAction = async () => {
    try {
      await completeProject({
        tenant: tenant!,
        projectId: effectiveProjectId,
      }).unwrap();
      toast.success('Project completed. Evaluation started.');
    } catch (error) {
      toast.error('Failed to mark project as complete');
    }
  };

  const handleStartEvaluationAction = async () => {
    const hasMembers = (project.members?.length ?? 0) > 0;
    const hasSkills = (project.expected_skills?.length ?? 0) > 0;

    if (!hasMembers || !hasSkills) {
      setConfirmConfig({
        title: 'Evaluation Requirements Not Met',
        description: `To start an evaluation cycle, the project must have:
${!hasMembers ? '\n• At least one project member/learner' : ''}
${!hasSkills ? '\n• At least one skill defined' : ''}

Please add the missing information before proceeding.`,
        confirmText: 'Understood',
        cancelText: 'Close',
        onConfirm: () => setConfirmOpen(false),
      });
      setConfirmOpen(true);
      return;
    }

    try {
      if (isExternal) {
        await startExternalEvaluation({
          projectId: effectiveProjectId,
        }).unwrap();
      } else {
        await startEvaluation({
          tenant: tenant!,
          projectId: effectiveProjectId,
        }).unwrap();
      }
      toast.success('Evaluation cycle started successfully');
      setShowEvaluation(true);
    } catch (error) {
      toast.error('Failed to start evaluation');
    }
  };

  const handleMarkAsInProgressAction = async () => {
    try {
      await markAsInProgress({
        tenant: tenant!,
        projectId: effectiveProjectId,
      }).unwrap();
      toast.success('Project marked as In Progress.');
    } catch (error) {
      toast.error('Failed to revert project status');
    }
  };

  const handleSDGSOnChange = React.useCallback(
    async (sdg: number | null) => {
      if (!sdg) {
        toast.warning("SDG can't be empty");
        return;
      }
      const currentSdgs = (project?.sdgs ?? [])
        .map((x) => (typeof x === 'string' ? Number(x) : x))
        .filter((n): n is number => Number.isFinite(n));

      if (currentSdgs.includes(sdg)) {
        toast.warning('SDG already exists');
        return;
      }

      try {
        await addOrUpdateSDGS({
          projectId: isExternal && project.project_id ? project.project_id : project.id,
          resourceId: isExternal ? (project.project_id ? project.id : -1) : undefined,
          tenant: tenant!,
          data: { sdgs: [...currentSdgs, sdg] },
        }).unwrap();
      }finally {
      }
    },
    [addOrUpdateSDGS, tenant, project?.id, project?.sdgs]
  );

  const isProjectMember = project.members?.some((m) => m.candidate === currentUser?.id) || (project).public_project_candidates?.some((pc) => pc.candidate.id === currentUser?.id);

  // Handler for adding multiple SDGs at once
  const handleMultipleSDGSChange = React.useCallback(
    async (sdgs: number[]) => {
      if (!sdgs.length) {
        toast.warning('No SDGs selected');
        return;
      }
      const currentSdgs = (project?.sdgs ?? [])
        .map((x) => (typeof x === 'string' ? Number(x) : x))
        .filter((n): n is number => Number.isFinite(n));

      // Filter out any that already exist
      const newSdgs = sdgs.filter((sdg) => !currentSdgs.includes(sdg));

      if (newSdgs.length === 0) {
        toast.warning('All selected SDGs already exist');
        return;
      }

      try {
        await addOrUpdateSDGS({
          projectId: isExternal && project.project_id ? project.project_id : project.id,
          resourceId: isExternal ? (project.project_id ? project.id : -1) : undefined,
          tenant: tenant!,
          data: { sdgs: [...currentSdgs, ...newSdgs] },
        }).unwrap();
        toast.success(`Added ${newSdgs.length} SDG(s)`);
      } catch {
        toast.error('Failed to add SDGs');
      }
    },
    [addOrUpdateSDGS, tenant, project.id, project.sdgs]
  );

  const handleRemoveSDG = React.useCallback(
    async (sdg: number) => {
      const currentSdgs = (project.sdgs ?? [])
        .map((x) => (typeof x === 'string' ? Number(x) : x))
        .filter((n): n is number => Number.isFinite(n));

      const nextSdgs = currentSdgs.filter((g) => g !== sdg);

      try {
        await addOrUpdateSDGS({
          projectId: isExternal && project.project_id ? project.project_id : project.id,
          resourceId: isExternal ? (project.project_id ? project.id : -1) : undefined,
          tenant: tenant!,
          data: { sdgs: nextSdgs },
        }).unwrap();

        toast.success('SDG removed');
      } catch {
        toast.error('Failed to remove SDG');
      }
    },
    [addOrUpdateSDGS, tenant, project.id, project.sdgs]
  );

  // school can be array OR object (safe)
  const school = React.useMemo(() => {
    const s = project.assigned_schools as any;
    return Array.isArray(s) ? s[0] : s;
  }, [project.assigned_schools]);

  const headerLoading =
    (thumbnailLoading ||
      updateThumbnailExternalLoading ||
      assignLoading ||
      deletingLoading ||
      duplicateLoading) &&
    selectedProject?.id === project.id;

  return (
    <>
      <ShowBoards show={showBoards} onClose={setShowBoards} />

      <AvatarEditor
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSave={updateThumbnailBanner}
        image={image}
        borderRadius={0}
        aspectRatio={1.5}
      />

      <ConfirmDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        config={confirmConfig as ConfirmDialogConfig | null}
        loading={deletingLoading}
      />

      <ProjectCuHandler
        openFor={openFor}
        title="Update the Project"
        projectType={isExternal ? 'EXTERNAL' : 'INTERNAL'}
        open={showUpdateProject}
        setOpen={setShowUpdateProject}
        setOpenFor={setOpenFor}
        project={selectedProject ?? undefined}
      />

      <AssignMentor
        open={!!mentorSheetProject}
        projectName={mentorSheetProject?.name}
        currentUserId={currentUser?.id}
        clientsLoading={false}
        onClose={() => setMentorSheetProject(null)}
        onSelect={handleAssignMentor}
      />

      <WithLoadingErrorWrapper
        loading={headerLoading}
        error={thumbnailError as ApiErrorResponse}
      >
        <div className="space-y-6">
          <Card className="overflow-hidden border-0 bg-card/50 backdrop-blur-sm shadow-xl shadow-black/5 p-0">
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
              style={{
                backgroundImage: `url('${project.project_banner}')`, // change path
              }}
            />

            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-black/40" />

            {/* Soft Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-transparent" />

            {/* Your Existing Content */}
            <div className="relative z-10">
              {/* keep everything inside here exactly as it is */}
            </div>

            <div className="absolute inset-x-0 top-0 h-full bg-muted/10 backdrop-blur-[2px]" />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />

            <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between p-4 sm:p-5 md:p-6">
              <Badge
                variant="outline"
                className={`
                    ${getStatusColor(project.project_status)}
                    rounded-full border-0 px-3.5 py-1.5 text-xs font-semibold
                    backdrop-blur-xl shadow-lg shadow-black/5
                    flex items-center gap-2
                  `}
              >
                <div className="relative flex h-2 w-2">
                  <div className="relative inline-flex h-2 w-2 rounded-full bg-current opacity-80" />
                </div>
                {getStatusLabel(project.project_status)}
              </Badge>

              <div className="flex items-center gap-2">
                {!isStudent && (
                  <ProjectMenu
                    open={openMenuId === project.id}
                    onOpenChange={(next) =>
                      setOpenMenuId(next ? project.id : null)
                    }
                    project={project}
                    items={buildProjectMenuItems((searchParams.get('type') as 'INTERNAL' | 'EXTERNAL') || 'INTERNAL', {
                      onEdit: handleEdit,
                      onChangeThumbnail: handleThumbnailChange,
                      onDuplicate: handleDuplicate,
                      onAssignMentor: (p) => setMentorSheetProject(p),
                      onDelete: () => {
                        handleDelete(project, 'details');
                      },
                      onMoveToBoard: (project) => setShowBoards(project),
                    }, isCompany)}
                  />
                )}
              </div>
            </div>

            <div className="relative -mt-16 sm:-mt-20 px-4 pb-6 sm:px-6 md:px-8 lg:px-10">
              <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between pt-24 sm:pt-32">
                <div className="flex items-end gap-4 sm:gap-5">
                  <div className="min-w-0 pb-2 mt-5">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight text-balance break-after-all">
                      {project.name ?? 'Untitled Project'}
                    </h1>
                    <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                      <Building className="h-4 w-4 shrink-0" />
                      <span className="truncate font-medium">
                        {school?.name ?? 'School'}
                      </span>
                    </p>

                    {project?.sdgs?.length ? (
                      <WithLoadingErrorWrapper
                        loading={sdgsLoading}
                        error={sdgsError as ApiErrorResponse}
                      >
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          {(project.sdgs ?? []).map((raw) => {
                            const g = Number(raw);
                            if (!Number.isFinite(g)) return null;

                            return (
                              <div
                                key={g}
                                className="group relative flex items-center gap-2 rounded-md border border-border/60 bg-background/90 px-3 py-1.5 backdrop-blur-sm shadow-sm transition-all duration-200 hover:shadow-md hover:border-border hover:bg-background"
                                title={`SDG ${g}: ${SDG_LABELS[g]}`}
                              >
                                <img
                                  src={sdgIconSrc(g) || '/placeholder.svg'}
                                  alt={`SDG ${g}`}
                                  className="h-5 w-5"
                                  loading="lazy"
                                />
                                <span className="hidden sm:inline text-xs font-semibold text-foreground">
                                  {SDG_LABELS[g]}
                                </span>
                                {!isStudent && (!isExternal || isCompany) && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveSDG(g)}
                                    className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground opacity-0 transition-all duration-200 hover:bg-destructive/15 hover:text-destructive group-hover:opacity-100"
                                    aria-label={`Remove SDG ${g}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </WithLoadingErrorWrapper>
                    ) : (
                      <p className="mt-4 text-sm text-muted-foreground/70 italic">
                        No SDGs selected yet
                      </p>
                    )}
                  </div>
                </div>

                <div className="hidden shrink-0 gap-3 md:flex">
                  {!isStudent && (!isExternal || isCompany) && (
                    <AddSdgsDialog
                      onChange={handleSDGSOnChange}
                      onChangeMultiple={handleMultipleSDGSChange}
                      existingSDGs={(project.sdgs ?? [])
                        .map((x) => (typeof x === 'string' ? Number(x) : x))
                        .filter((n): n is number => Number.isFinite(n))}
                    />
                  )}
                  {canShowPublishAction && (
                    <Button
                      size="default"
                      onClick={() => setIsAssignDrawerOpen(true)}
                    >
                      Publish Project
                    </Button>
                  )}
                  {isMentor && (canStart || (canComplete && !isExternal)) && (
                    <Button
                      size="default"
                      onClick={
                        canStart
                          ? handleStartProjectAction
                          : handleCompleteProjectAction
                      }
                      disabled={isStartingProject || isCompletingProject || isStartingExternalProject}
                    >
                      {isStartingProject || isCompletingProject || isStartingExternalProject ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : canStart ? (
                        <Play className="h-4 w-4" />
                      ) : (
                        <CircleCheck className="h-4 w-4" />
                      )}
                      <span className="font-semibold ml-2">
                        {canStart ? 'Start Project' : 'Complete Project'}
                      </span>
                    </Button>
                  )}
                  {isMentor && canComplete && (
                    <Button
                      size="default"
                      variant="outline"
                      onClick={handleStartEvaluationAction}
                      disabled={isStartingEvaluation || isStartingExternalEvaluation}
                    >
                      {isStartingEvaluation || isStartingExternalEvaluation ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      <span className="font-semibold ml-2">
                        Start Evaluation
                      </span>
                    </Button>
                  )}
                  {isMentor && canReopen && (
                    <Button
                      size="default"
                      variant="outline"
                      onClick={handleMarkAsInProgressAction}
                      disabled={isMarkingInProgress}
                    >
                      {isMarkingInProgress ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4" />
                      )}
                      <span className="font-semibold ml-2">
                        Mark as In Progress
                      </span>
                    </Button>
                  )}
                </div>
              </div>

              {/* Progress Bar for Started Projects */}
              {Number(project.project_status) >= 4 && (
                <div className="mt-8 space-y-2">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-muted-foreground flex items-center gap-1.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                      Project Progress
                    </span>
                    <span className="text-primary bg-primary/10 px-2 py-0.5 rounded-full ring-1 ring-primary/20">
                      {project.tasks_completed_percentage?.toFixed(2) ?? 0}% Complete
                    </span>
                  </div>
                  <Progress
                    value={project.tasks_completed_percentage ?? 0}
                    className="h-2.5 bg-primary/5 ring-1 ring-primary/10"
                  />
                </div>
              )}
            </div>
          </Card>

          <div className="flex gap-3 md:hidden">
            {!isStudent && (!isExternal || isCompany) && (
              <AddSdgsDialog
                onChange={handleSDGSOnChange}
                onChangeMultiple={handleMultipleSDGSChange}
                existingSDGs={(project.sdgs ?? [])
                  .map((x) => (typeof x === 'string' ? Number(x) : x))
                  .filter((n): n is number => Number.isFinite(n))}
              />
            )}
            {canShowPublishAction && (
              <Button
                size="lg"
                className="rounded-full"
                onClick={() => setIsAssignDrawerOpen(true)}
              >
                Publish Project
              </Button>
            )}
            {isMentor && (canStart || (canComplete && !isExternal)) && (
              <Button
                size="lg"
                className="rounded-full"
                onClick={
                  canStart
                    ? handleStartProjectAction
                    : handleCompleteProjectAction
                }
                disabled={isStartingProject || isCompletingProject || isStartingExternalProject}
              >
                {isStartingProject || isCompletingProject || isStartingExternalProject ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : canStart ? (
                  <Play className="h-5 w-5" />
                ) : (
                  <CircleCheck className="h-5 w-5" />
                )}
                <span className="font-semibold ml-2">
                  {canStart ? 'Start Project' : 'Complete Project'}
                </span>
              </Button>
            )}
            {isMentor && canComplete && (
              <Button
                size="lg"
                variant="outline"
                className="rounded-full flex-1"
                onClick={handleStartEvaluationAction}
                disabled={isStartingEvaluation || isStartingExternalEvaluation}
              >
                {isStartingEvaluation || isStartingExternalEvaluation ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Play className="h-5 w-5" />
                )}
                <span className="font-semibold ml-2">Start Evaluation</span>
              </Button>
            )}
            {isMentor && canReopen && (
              <Button
                size="lg"
                variant="outline"
                className="rounded-full"
                onClick={handleMarkAsInProgressAction}
                disabled={isMarkingInProgress}
              >
                {isMarkingInProgress ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <RotateCcw className="h-5 w-5" />
                )}
                <span className="font-semibold ml-2">Mark as In Progress</span>
              </Button>
            )}
          </div>
        </div>
      </WithLoadingErrorWrapper>

      <EvaluationModal
        open={showEvaluation}
        onOpenChange={setShowEvaluation}
        projectId={effectiveProjectId}
        members={project.members}
        skills={project.expected_skills}
        isExternal={isExternal}
      />
      {evaluationRequired && !showEvaluation && !hideEvaluationPrompt && isProjectMember && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(92vw,480px)]">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50/95 p-4 shadow-[0_20px_70px_rgba(15,23,42,0.18)] backdrop-blur-xl transition-colors dark:border-slate-700/70 dark:bg-slate-900/95 dark:shadow-[0_20px_70px_rgba(0,0,0,0.45)]">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-cyan-500/10 dark:from-primary/15 dark:to-cyan-400/10" />
            <div className="relative flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/15 dark:bg-primary/15 dark:ring-primary/20">
                <CircleCheck className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                      Skills evaluation requested!
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                      Your mentor or project lead has requested self and peer skills evaluation. Please continue to the evaluation modal to proceed.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="h-8 w-8 shrink-0 rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                    onClick={() => setHideEvaluationPrompt(true)}
                    aria-label="Dismiss evaluation prompt"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="mt-4 flex items-center justify-end">
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-full px-4 font-semibold shadow-sm"
                    onClick={() => setShowEvaluation(true)}
                  >
                    Continue to skills evaluation
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Sheet open={isAssignDrawerOpen} onOpenChange={handleAssignDrawerChange}>
        <SheetContent side="right" className="w-full p-0 sm:max-w-[min(96vw,1180px)]">
          <SheetHeader className="border-b border-border/60 px-4 py-4 sm:px-6 sm:py-5">
            <div className="flex items-center gap-3 pr-8">
              <Button type="button" variant="ghost" size="icon-sm" className="h-8 w-8 rounded-full" onClick={() => handleAssignDrawerChange(false)}>
                <X className="h-4 w-4" />
              </Button>
              <SheetTitle className="text-xl sm:text-2xl">Select institute(s)</SheetTitle>
            </div>
          </SheetHeader>
          <div className="space-y-6 overflow-y-auto px-4 py-4 sm:space-y-8 sm:px-6 sm:py-6">
            <div className="rounded-2xl border border-sky-200 bg-sky-50/90 p-4 sm:p-5 dark:border-sky-900/60 dark:bg-sky-950/30">
              <label className="flex items-start gap-3">
                <Checkbox checked={assignToCogknitAdmin} onCheckedChange={(checked) => { const nextChecked = checked === true; setAssignToCogknitAdmin(nextChecked); if (nextChecked) setSelectedInstituteId(null); }} className="mt-1" />
                <div>
                  <p className="font-medium text-foreground">Assign to Cogknit admin</p>
                  <p className="mt-2 text-sm leading-6 sm:leading-7 text-muted-foreground">If you do not know which institute to select, you can temporarily assign this project to Cogknit admin. Cogknit admin will review it and reassign to relevant institute(s).</p>
                </div>
              </label>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 overflow-x-auto border-b border-border/60 text-sm font-medium">
                <button type="button" className={`border-b-2 pb-3 transition-colors ${assignDrawerTab === 'scopes' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} onClick={() => setAssignDrawerTab('scopes')}>Scopes</button>
                <span className="pb-3 text-muted-foreground">|</span>
                <button type="button" className={`border-b-2 pb-3 transition-colors ${assignDrawerTab === 'institutes' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} onClick={() => { setAssignDrawerTab('institutes'); setSelectedScopeId(null); }}>Institutes</button>
              </div>
              {assignDrawerTab === 'scopes' ? (
                scopeCards.length ? (
                  <div className="grid gap-4 lg:grid-cols-2">
                    {scopeCards.map((scope) => (
                      <button key={scope.id} type="button" onClick={() => { setSelectedScopeId(scope.id); setAssignDrawerTab('institutes'); }} className="rounded-2xl border border-border/60 bg-background p-5 text-left shadow-sm transition-all hover:border-primary/40 hover:shadow-md">
                        <div className="space-y-4">
                          <div><p className="text-lg font-semibold">{scope.name}</p>{scope.description ? <p className="mt-1 text-sm text-muted-foreground">{scope.description}</p> : null}</div>
                          <div className="flex flex-wrap gap-3">
                            {scope.institutes.map((client) => (
                              <div key={client.id} className="inline-flex items-center gap-2 rounded-full bg-muted/70 px-3 py-2 text-sm text-foreground">
                                <Avatar className="h-7 w-7 rounded-full"><AvatarImage src={client.logo_url || undefined} alt={client.name} /><AvatarFallback className="rounded-full text-[10px] font-semibold">{client.name.split(' ').slice(0,2).map(p=>p.charAt(0).toUpperCase()).join('') || 'NA'}</AvatarFallback></Avatar>
                                <span>{client.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : <div className="rounded-2xl border border-dashed border-border/60 px-6 py-14 text-center text-sm text-muted-foreground">No scopes available right now.</div>
              ) : visibleInstitutes.length ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {visibleInstitutes.map((client) => {
                    const isSelected = selectedInstituteId === client.id;
                    const scopeNames = client.client_scope.map((scope) => scope.name).join(', ');
                    return (
                      <button key={client.id} type="button" onClick={() => handleInstituteSelect(client.id)} disabled={assignToCogknitAdmin} className={`rounded-2xl border bg-background p-4 text-left shadow-sm transition-all ${isSelected ? 'border-primary ring-2 ring-primary/15' : 'border-border/60 hover:border-primary/30 hover:shadow-md'} ${assignToCogknitAdmin ? 'cursor-not-allowed opacity-50' : ''}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center sm:gap-4">
                            <Avatar className="h-14 w-14 shrink-0 rounded-2xl border border-border/50 sm:h-16 sm:w-16"><AvatarImage src={client.logo_url || undefined} alt={client.name} /><AvatarFallback className="rounded-2xl text-sm font-semibold">{client.name.split(' ').slice(0,2).map(p=>p.charAt(0).toUpperCase()).join('') || 'NA'}</AvatarFallback></Avatar>
                            <div className="min-w-0 flex-1"><p className="line-clamp-2 text-base font-semibold sm:text-xl">{client.name}</p>{scopeNames ? <p className="mt-1 line-clamp-2 text-sm text-muted-foreground sm:mt-2">{scopeNames}</p> : null}</div>
                          </div>
                          <Checkbox checked={isSelected} className="mt-1 shrink-0" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : <div className="rounded-2xl border border-dashed border-border/60 px-6 py-14 text-center text-sm text-muted-foreground">No institutes available for this filter.</div>}
            </div>
          </div>
          <SheetFooter className="border-t border-border/60 px-4 py-4 sm:px-6">
            <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <Button type="button" variant="outline" onClick={() => handleAssignDrawerChange(false)} disabled={isAssigningProject} className="w-full sm:w-auto">Cancel</Button>
              <Button type="button" onClick={handleAssignSubmit} disabled={!canSubmitAssignment || isAssigningProject} className="w-full sm:w-auto">{isAssigningProject ? 'Submitting...' : 'Submit'}</Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};
